/**
 * KpiCard Component - Enhanced KPI card with animations and trends
 */

import React from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Card } from '../ui'

const KpiCard = ({
  label,
  value,
  delta,
  type = 'default',
  icon = null,
  className = '',
  loading = false,
  format = (v) => v,
  ...props
}) => {
  const positive = type === 'income' || type === 'profit' || type === 'revenue'
  const safeDelta = isNaN(delta) ? 0 : delta
  
  const trendColor = safeDelta > 0 ? 'text-success' : safeDelta < 0 ? 'text-error' : 'text-muted'
  const TrendIcon = safeDelta > 0 ? TrendingUp : safeDelta < 0 ? TrendingDown : Minus

  // Color variants
  const variants = {
    default: 'border-neutral-200',
    income: 'border-l-4 border-l-success',
    expense: 'border-l-4 border-l-error',
    profit: 'border-l-4 border-l-brand',
    revenue: 'border-l-4 border-l-brand',
    growth: 'border-l-4 border-l-gold',
  }

  if (loading) {
    return (
      <Card className={`p-5 ${variants[type] || variants.default} ${className}`}>
        <div className="space-y-3">
          <div className="h-4 w-24 bg-neutral-100 rounded shimmer" />
          <div className="h-8 w-32 bg-neutral-100 rounded shimmer" />
          <div className="h-3 w-20 bg-neutral-100 rounded shimmer" />
        </div>
      </Card>
    )
  }

  return (
    <Card 
      className={`p-5 transition-all duration-200 hover:shadow-md ${variants[type] || variants.default} ${className}`}
      {...props}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            {label}
          </p>
          <p className="mt-2 text-2xl font-bold text-ink font-mono">
            {format(value)}
          </p>
        </div>
        {icon && (
          <div className="w-10 h-10 bg-brand-soft rounded-lg flex items-center justify-center shrink-0">
            {icon}
          </div>
        )}
      </div>

      {delta !== undefined && (
        <div className="mt-3 flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${trendColor}`}>
            <TrendIcon size={12} />
            {safeDelta > 0 ? '+' : ''}{safeDelta}%
          </span>
          <span className="text-xs text-muted">vs last period</span>
        </div>
      )}
    </Card>
  )
}

KpiCard.displayName = 'KpiCard'

export default KpiCard